<script>
  import { emitter } from '@/main.js';

  export default {
    name: 'EndlessScroll',
    props: {
      triggerDistance: {
        type: Number,
        default: 500
      },
      loadingImage: {
        type: String,
        default: null
      }
    },
    data() {
      return {
        pageCount: 0,
        page: 0,
        hasLoaded: false,
        justLoadedNewResults: false,
        loadingTimeout: null,
        observer: null,
        triggerElement: null,
        visibilityCheckInterval: null,
        lastObserverTrigger: null,
        isPageRestored: false,
        pageshowHandler: null,
        isContentRendering: false,
        contentRenderingTimeout: null,
        comparePidBlocksScroll: false,
        paused: false
      };
    },
    computed: {
      shouldShowLoading() {
        // Show loading if there are more pages or content is still rendering
        return this.page < this.pageCount || this.isContentRendering;
      }
    },
    watch: {
      pageCount(newVal) {
        if (newVal > 0 && !this.observer) {
          this.$nextTick(() => {
            this.setupIntersectionObserver();
          });
        }
      }
    },
    mounted() {
      this.triggerElement = this.$el || document.querySelector('#fs-collection-endlessScroll');

      emitter.on('fs-update-pager', this.handlePagerUpdate);
      emitter.on('fs-new-search-reset', this.handleNewSearchReset);
      emitter.on('fs-load-more-error', this.handleLoadMoreError);
      emitter.on('fs-load-more-retry', this.handleLoadMoreRetry);
      this.handleFilterReset = () => {
        this.paused = false;
        this.page = 0;
        this.pageCount = 0;
        this.hasLoaded = true;
        this.justLoadedNewResults = true;
        this.isContentRendering = false;
        if (this.contentRenderingTimeout) {
          clearTimeout(this.contentRenderingTimeout);
          this.contentRenderingTimeout = null;
        }
        if (this.loadingTimeout) {
          clearTimeout(this.loadingTimeout);
          this.loadingTimeout = null;
        }
      };
      emitter.on('fs-filter-reset', this.handleFilterReset);
      this.comparePidActiveHandler = active => {
        this.comparePidBlocksScroll = active === true;
      };
      emitter.on('plp-compare-pid-active', this.comparePidActiveHandler);

      // Handle page restoration from bfcache (back/forward navigation)
      this.pageshowHandler = event => {
        try {
          if (event && event.persisted) {
            // Page was restored from bfcache
            this.isPageRestored = true;
            // Use requestAnimationFrame to ensure DOM is ready
            requestAnimationFrame(() => {
              if (this.$el) {
                this.$nextTick(() => {
                  // Immediately check if we need to load more products
                  this.checkAndLoadIfNeeded();
                });
              }
            });
          }
        } catch (error) {
          // Silently handle any errors to avoid breaking bfcache
          console.debug('[EndlessScroll] pageshow handler error:', error);
        }
      };
      // Use addEventListener to avoid conflicts with existing handlers
      if (typeof window !== 'undefined' && window.addEventListener) {
        window.addEventListener('pageshow', this.pageshowHandler, { passive: true });
      }

      this.$nextTick(() => {
        this.setupIntersectionObserver();
        // Start visibility check immediately if page was restored, otherwise wait
        if (this.isPageRestored) {
          this.startVisibilityCheck();
          this.checkAndLoadIfNeeded();
        } else {
          // Check if user is already scrolled near bottom (likely from back navigation)
          const scrollPosition = window.scrollY || window.pageYOffset;
          const documentHeight = document.documentElement.scrollHeight;
          const windowHeight = window.innerHeight || document.documentElement.clientHeight;
          const isNearBottom = scrollPosition + windowHeight >= documentHeight - this.triggerDistance * 2;

          if (isNearBottom) {
            this.startVisibilityCheck();
            this.checkAndLoadIfNeeded();
          } else {
            setTimeout(() => {
              this.startVisibilityCheck();
            }, 2000);
          }
        }
      });
    },
    beforeUnmount() {
      if (this.loadingTimeout) {
        clearTimeout(this.loadingTimeout);
      }

      if (this.contentRenderingTimeout) {
        clearTimeout(this.contentRenderingTimeout);
      }

      if (this.visibilityCheckInterval) {
        clearInterval(this.visibilityCheckInterval);
        this.visibilityCheckInterval = null;
      }

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.pageshowHandler) {
        window.removeEventListener('pageshow', this.pageshowHandler);
      }

      emitter.off('fs-update-pager', this.handlePagerUpdate);
      emitter.off('fs-new-search-reset', this.handleNewSearchReset);
      emitter.off('fs-load-more-error', this.handleLoadMoreError);
      emitter.off('fs-load-more-retry', this.handleLoadMoreRetry);
      if (this.handleFilterReset) {
        emitter.off('fs-filter-reset', this.handleFilterReset);
      }
      if (this.comparePidActiveHandler) {
        emitter.off('plp-compare-pid-active', this.comparePidActiveHandler);
      }
    },
    methods: {
      handlePagerUpdate(e) {
        this.paused = false;
        this.pageCount = e.pageCount;
        this.page = e.page;
        this.hasLoaded = false;

        if (this.loadingTimeout) {
          clearTimeout(this.loadingTimeout);
          this.loadingTimeout = null;
        }

        // Clear any existing content rendering timeout
        if (this.contentRenderingTimeout) {
          clearTimeout(this.contentRenderingTimeout);
        }

        // Keep loading visible until content finishes rendering (helps slow connections)
        if (this.page >= this.pageCount) {
          this.isContentRendering = false;
        } else {
          // Wait for content to render before hiding loading indicator
          this.contentRenderingTimeout = setTimeout(() => {
            this.isContentRendering = false;
            this.contentRenderingTimeout = null;
          }, 1000);
        }

        if (this.pageCount > 0 && !this.observer) {
          this.$nextTick(() => {
            this.setupIntersectionObserver();
          });
        }

        this.justLoadedNewResults = true;
        // Reduce delay if page was restored from cache
        const delay = this.isPageRestored ? 100 : 500;
        setTimeout(() => {
          this.justLoadedNewResults = false;

          this.$nextTick(() => {
            const currentElement = this.$el || document.querySelector('#fs-collection-endlessScroll');
            if (currentElement && currentElement !== this.triggerElement) {
              this.triggerElement = currentElement;
            }

            if (this.observer) {
              if (currentElement && currentElement !== this.triggerElement) {
                this.observer.disconnect();
                this.triggerElement = currentElement;
                this.observer.observe(this.triggerElement);
              } else if (currentElement && !this.observer) {
                this.setupIntersectionObserver();
              }
            } else if (currentElement && this.pageCount > 0) {
              this.setupIntersectionObserver();
            }

            if (this.shouldShowLoading && this.isElementVisible() && !this.hasLoaded && this.page < this.pageCount) {
              this.setPage();
            }
          });
        }, delay);
      },

      handleNewSearchReset(e) {
        this.paused = false;
        this.page = e.page;
        this.hasLoaded = false;
        this.justLoadedNewResults = true;
      },

      handleLoadMoreError() {
        this.paused = true;
        this.hasLoaded = false;
        this.isContentRendering = false;
        if (this.loadingTimeout) {
          clearTimeout(this.loadingTimeout);
          this.loadingTimeout = null;
        }
        if (this.contentRenderingTimeout) {
          clearTimeout(this.contentRenderingTimeout);
          this.contentRenderingTimeout = null;
        }
      },

      handleLoadMoreRetry() {
        this.paused = false;
        this.hasLoaded = false;
        this.justLoadedNewResults = false;
        this.setPage();
      },

      setupIntersectionObserver() {
        if (!this.triggerElement) {
          this.triggerElement = this.$el || document.querySelector('#fs-collection-endlessScroll');
        }

        if (!this.triggerElement) {
          console.warn('[EndlessScroll] Trigger element not found, observer not created');
          return;
        }

        if (this.isElementVisible() && this.shouldShowLoading && !this.hasLoaded && !this.justLoadedNewResults) {
          this.lastObserverTrigger = Date.now();
          this.setPage();
        }

        if (this.observer) {
          this.observer.disconnect();
        }

        const rootMargin = `${this.triggerDistance}px 0px ${this.triggerDistance}px 0px`;

        this.observer = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                this.lastObserverTrigger = Date.now();
                this.setPage();
              }
            });
          },
          {
            root: null,
            rootMargin: rootMargin,
            threshold: 0
          }
        );

        this.observer.observe(this.triggerElement);
      },

      isElementVisible() {
        const element = this.$el || this.triggerElement || document.querySelector('#fs-collection-endlessScroll');
        if (!element) {
          return false;
        }

        const rect = element.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const windowWidth = window.innerWidth || document.documentElement.clientWidth;

        return rect.top < windowHeight + this.triggerDistance && rect.bottom > -this.triggerDistance && rect.left < windowWidth && rect.right > 0;
      },

      startVisibilityCheck() {
        if (this.visibilityCheckInterval) {
          clearInterval(this.visibilityCheckInterval);
        }

        this.visibilityCheckInterval = setInterval(() => {
          if (this.shouldShowLoading && this.isElementVisible()) {
            const observerRecentlyTriggered = this.lastObserverTrigger && Date.now() - this.lastObserverTrigger < 2000;

            if (!this.hasLoaded && !this.justLoadedNewResults && this.page < this.pageCount && !observerRecentlyTriggered) {
              this.setPage();
            }
          }
        }, 1000);
      },

      setPage() {
        if (this.comparePidBlocksScroll || this.paused) {
          return;
        }
        if (this.page < this.pageCount && this.hasLoaded === false && !this.justLoadedNewResults) {
          const pageIndex = this.page + 1;
          const pagination = document.getElementById('fs-collection-pagination');

          this.isContentRendering = true;

          emitter.emit('fs-update-page', pageIndex);

          this.loadingTimeout = setTimeout(() => {
            if (this.hasLoaded) {
              this.hasLoaded = false;
              this.loadingTimeout = null;
            }
          }, 3000);

          this.hasLoaded = true;

          if (pagination) {
            pagination.style.opacity = 0;
          }
        }
      },

      checkAndLoadIfNeeded() {
        // Check if we need to load more products after page restoration
        if (this.shouldShowLoading && this.isElementVisible() && !this.hasLoaded && !this.justLoadedNewResults && this.page < this.pageCount) {
          this.setPage();
        }

        // Ensure observer is set up
        if (this.pageCount > 0 && !this.observer) {
          this.setupIntersectionObserver();
        }
      }
    }
  };
</script>

<template>
  <div class="endless-scroll-loading" :style="{ minHeight: page < pageCount && !paused ? 'auto' : '1px' }">
    <div v-if="page < pageCount && !paused">
      <img v-if="loadingImage" :src="loadingImage" alt="Loading more products..." class="endless-scroll-loading-image" />
      <span v-else>Loading...</span>
    </div>
  </div>
</template>

<style scoped>
  .endless-scroll-loading {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem;
    width: 100%;
  }

  .endless-scroll-loading-image {
    max-width: 100%;
    height: auto;
    display: block;
  }
</style>
